// Greedy lane packing for the timeline's conflict bars: most significant first, each into the first lane
// where it fits with a small gap; a bar keeps its label only if the label fits inside it.

import type { Conflicts } from "../lib/data";

export interface LaneBar { i: number; lane: number; y: number; x0: number; x1: number; segs: [number, number][]; label: string; lx: number }

const GAP = 4;

export function packLanes(c: Conflicts, candidates: number[], a: number, b: number, w: number, lanes: number,
  top: number, laneH: number, laneGap: number, ctx?: CanvasRenderingContext2D): LaneBar[] {
  const px = (x: number) => ((x - a) / (b - a)) * w;
  const occupied: [number, number][][] = Array.from({ length: lanes }, () => []);
  const out: LaneBar[] = [];
  for (const i of candidates) {
    const runs = c.runs[i];
    let segs: [number, number][] = runs
      ? runs.map(([y0, y1]) => [px(Math.max(y0, c.t0[i])), px(Math.min(y1 + 1, c.t1[i]))])
      : [[px(c.t0[i]), px(c.t1[i])]];
    segs = segs.map(([x0, x1]) => [Math.max(-5, x0), Math.min(w + 5, Math.max(x1, x0 + 2))] as [number, number])
      .filter(([x0, x1]) => x1 > -5 && x0 < w + 5);
    if (!segs.length) continue;
    const x0 = segs[0][0], x1 = segs[segs.length - 1][1];
    let lane = -1;
    for (let l = 0; l < lanes; l++) {
      if (occupied[l].every(([s, e]) => x1 + GAP < s || x0 - GAP > e)) { lane = l; break; }
    }
    if (lane < 0) continue;
    occupied[lane].push([x0, x1]);
    const name = c.name[i];
    let label = "";
    const inner = Math.min(x1, w) - Math.max(x0, 0) - 8;
    if (inner > 24 && ctx) {
      ctx.font = "600 10.5px 'Inter Variable', system-ui, sans-serif";
      label = fit(ctx, name, inner);
    }
    out.push({ i, lane, y: top + lane * (laneH + laneGap), x0, x1, segs, label, lx: Math.max(x0, 0) + 4 });
  }
  return out;
}

function fit(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + "…").width <= max) lo = mid;
    else hi = mid - 1;
  }
  return lo >= Math.min(12, Math.ceil(text.length * 0.6)) ? text.slice(0, lo).trimEnd() + "…" : "";
}
